import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Sparkles,
  XCircle,
  FileText,
  Image,
  BookOpen,
  RefreshCw,
  AlertTriangle,
  Eye,
  Tag,
  Route as RouteIcon,
  Brain,
  Lightbulb,
  Users,
  TrendingUp,
  Zap,
  Circle,
  PlusCircle,
  ArrowRight,
} from 'lucide-react';
import learningPathWorkflowService from '@/services/learning-path-workflow.service';
import { WorkflowStage } from '@/types/learning-path-workflow.types';
import { SkeletonLoader, CardSkeleton } from '@/components/ui/SkeletonLoader';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNING_PATH_CATALOG_PERMISSION, COURSE_GENERATION_PERMISSION } from '@/constants/constant-permissions';

interface GenerationStage {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
}

export default function WorkflowStatus() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const canWriteLP = useHasPermission([LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE]);
  const canReadLP = useHasPermission([LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_READ]);
  const canBuildCourse = useHasPermission([COURSE_GENERATION_PERMISSION.COURSE_GENERATION_WRITE]);
  const accessBothReadAndWrite = canReadLP || canWriteLP;
  const [isGenerating, setIsGenerating] = useState(true);
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stageElapsedTimes, setStageElapsedTimes] = useState<Record<string, number>>({});
  const userOpenedStagesRef = useRef<Set<string>>(new Set());

  // Generation stages configuration
  const stages: GenerationStage[] = useMemo(
    () => [
      {
        key: 'stage_1_jd_processing',
        label: 'Stage 1: File Processing',
        description: 'Extract text from uploaded files (PDF/DOCX/TXT)',
        icon: FileText,
        color: 'from-blue-500 to-indigo-600',
      },
      {
        key: 'stage_2_skill_extraction',
        label: 'Stage 2: Skills Extraction',
        description: 'AI analyzes job description and extracts required skills',
        icon: Brain,
        color: 'from-purple-500 to-pink-600',
      },
      {
        key: 'stage_3_metadata',
        label: 'Stage 3: Metadata Generation',
        description: 'AI generates title, description, outcomes, and tags',
        icon: Sparkles,
        color: 'from-green-500 to-teal-600',
      },
      {
        key: 'stage_4_images',
        label: 'Stage 4: Image Generation',
        description: 'AI generates banner and thumbnail images',
        icon: Image,
        color: 'from-amber-500 to-orange-600',
      },
      {
        key: 'stage_5_courses',
        label: 'Stage 5: Course Selection',
        description: 'AI selects and sequences relevant courses',
        icon: BookOpen,
        color: 'from-pink-500 to-rose-600',
      },
    ],
    []
  );

  // Fetch workflow status
  const { data: statusData, isLoading, error } = useQuery({
    queryKey: ['workflow-status', id],
    queryFn: () => learningPathWorkflowService.getWorkflowStatus(id!),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.data) return 3000; // Keep polling if no data yet
      const status = data.data.overall_status;
      // Poll while pending or processing
      return status === 'processing' || status === 'pending' ? 3000 : false;
    },
    enabled: !!id,
  });

  const status = statusData?.data;

  // Update isGenerating based on overall status
  useEffect(() => {
    if (status?.overall_status === 'completed' || status?.overall_status === 'failed') {
      setIsGenerating(false);
    } else if (status?.overall_status === 'processing' || status?.overall_status === 'pending') {
      setIsGenerating(true);
    }
  }, [status?.overall_status]);

  // Calculate overall elapsed time
  useEffect(() => {
    if (!status?.started_at) return;

    const updateElapsedTime = () => {
      const startTime = new Date(status.started_at!).getTime();
      const now = Date.now();
      // Ensure elapsed time is never negative - handle timing issues gracefully
      setElapsedTime(Math.max(0, now - startTime));
    };

    updateElapsedTime();

    if (isGenerating) {
      const interval = setInterval(updateElapsedTime, 1000);
      return () => clearInterval(interval);
    }
  }, [status?.started_at, isGenerating]);

  // Calculate elapsed time for each stage
  useEffect(() => {
    if (!status) return;

    const updateStageElapsedTimes = () => {
      const newTimes: Record<string, number> = {};

      stages.forEach((stage) => {
        const stageData = status[stage.key as keyof typeof status] as WorkflowStage | undefined;
        if (!stageData) return;

        const stageStatus = stageData.status?.toLowerCase();

        if (stageStatus === 'processing') {
          const startedAt = stageData.start_time;
          if (startedAt) {
            const startTime = new Date(startedAt).getTime();
            // Ensure elapsed time is never negative - handle timing issues gracefully
            newTimes[stage.key] = Math.max(0, Date.now() - startTime);
          }
        } else if ((stageStatus === 'completed' || stageStatus === 'failed') && stageData.duration_ms) {
          // Ensure stored duration is never negative
          newTimes[stage.key] = Math.max(0, stageData.duration_ms);
        }
      });

      setStageElapsedTimes(newTimes);
    };

    updateStageElapsedTimes();

    if (isGenerating) {
      const interval = setInterval(updateStageElapsedTimes, 1000);
      return () => clearInterval(interval);
    }
  }, [status, isGenerating, stages]);

  // Auto-expand active stage
  useEffect(() => {
    if (!status) return;

    stages.forEach((stage) => {
      const stageData = status[stage.key as keyof typeof status] as WorkflowStage | undefined;
      if (!stageData) return;

      const stageStatus = stageData.status?.toLowerCase();

      if (stageStatus === 'processing' && !userOpenedStagesRef.current.has(stage.key)) {
        setExpandedStages((prev) => {
          const newSet = new Set(prev);
          newSet.add(stage.key);
          return newSet;
        });
      }
    });
  }, [status, stages]);

  const toggleStageExpansion = (stageKey: string) => {
    setExpandedStages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stageKey)) {
        newSet.delete(stageKey);
        userOpenedStagesRef.current.delete(stageKey);
      } else {
        newSet.add(stageKey);
        userOpenedStagesRef.current.add(stageKey);
      }
      return newSet;
    });
  };

  const formatDuration = (ms: number) => {
    // Ensure duration is never negative - handle timing issues gracefully
    const safeDuration = Math.max(0, ms);
    const seconds = Math.floor(safeDuration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getStageStatus = (stageKey: string): WorkflowStage | null => {
    if (!status) return null;
    return (status[stageKey as keyof typeof status] as WorkflowStage) || null;
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-5.25rem)] bg-gradient-to-br from-gray-50 via-purple-50/30 to-pink-50/30">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate('/admin/learning-paths/workflow')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Workflows</span>
          </button>

          {/* Hero Header Skeleton */}
          <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 rounded-3xl shadow-2xl p-8 mb-6 relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <SkeletonLoader type="custom" width="56px" height="56px" className="rounded-xl bg-white/20" />
                <div className="flex-1">
                  <SkeletonLoader type="custom" width="300px" height="32px" className="mb-2 bg-white/20" />
                  <SkeletonLoader type="custom" width="250px" height="16px" className="bg-white/10" />
                </div>
              </div>
              <SkeletonLoader type="custom" width="100%" height="20px" className="bg-white/10" />
            </div>
          </div>

          {/* Metrics Skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-md p-5 border border-gray-200">
                <div className="flex items-center gap-3">
                  <SkeletonLoader type="custom" width="48px" height="48px" className="rounded-lg" />
                  <div className="flex-1">
                    <SkeletonLoader type="text" className="h-3 w-20 mb-2" />
                    <SkeletonLoader type="text" className="h-6 w-12" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stages Skeletons */}
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <CardSkeleton key={i} showImage={false} lines={2} className="border border-gray-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600 mb-4">The workflow you're looking for doesn't exist or has been deleted.</p>
          <button
            onClick={() => navigate('/admin/learning-paths/workflow')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-5.25rem)] bg-gradient-to-br from-gray-50 via-purple-50/30 to-pink-50/30">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/learning-paths/workflow')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Workflows</span>
          </button>

          {/* Hero Header */}
          <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 rounded-3xl shadow-2xl p-8 mb-6 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-transparent to-pink-600/20"></div>
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <RouteIcon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold">{status.workflow_name || 'AI Learning Path Workflow'}</h1>
                      <p className="text-purple-100 text-sm mt-1">
                        {status.workflow_desc || status.path_title || 'Processing workflow...'}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  className={`px-4 py-2 rounded-full backdrop-blur-sm border-2 font-semibold text-sm ${
                    status.overall_status === 'completed'
                      ? 'bg-green-500/20 border-green-300 text-white'
                      : status.overall_status === 'processing'
                      ? 'bg-yellow-500/20 border-yellow-300 text-white animate-pulse'
                      : status.overall_status === 'pending'
                      ? 'bg-blue-500/20 border-blue-300 text-white animate-pulse'
                      : 'bg-red-500/20 border-red-300 text-white'
                  }`}
                >
                  {status.overall_status === 'completed'
                    ? '✓ Completed'
                    : status.overall_status === 'processing'
                    ? '⚡ Processing'
                    : status.overall_status === 'pending'
                    ? '⏳ Starting...'
                    : '✗ Failed'}
                </div>
              </div>

              {/* Status Message */}
              <p className="text-purple-100 mb-4">
                {status.overall_status === 'processing' ? (
                  <span className="flex items-center gap-2">
                    <Zap className="w-4 h-4 animate-pulse" />
                    AI is crafting your learning path through {status.total_stages ?? 5} intelligent stages...
                  </span>
                ) : status.overall_status === 'completed' ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Your learning path has been successfully generated and is ready to publish
                  </span>
                ) : status.overall_status === 'pending' ? (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 animate-pulse" />
                    Workflow is initializing, AI generation will begin shortly...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Generation encountered an issue. Please check the stages below for details.
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Metrics Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-0.5">Courses</p>
                  <p className="text-xl font-bold text-gray-900">{status.total_courses || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-0.5">Completed Stages</p>
                  <p className="text-xl font-bold text-gray-900">
                    {status.completed_stages ?? 0}/{status.total_stages ?? 5}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-0.5">
                    {isGenerating ? 'Elapsed Time' : 'Total Duration'}
                  </p>
                  <p className="text-xl font-bold text-gray-900">{formatDuration(elapsedTime)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-5 border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-pink-100 to-rose-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-0.5">Progress</p>
                  <p className="text-xl font-bold text-gray-900">{status.progress_percentage}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Metadata Preview Card */}
          {status.path_title && (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <RouteIcon className="w-5 h-5 text-purple-600" />
                  <h2 className="text-lg font-bold text-gray-900">Learning Path Details</h2>
                </div>
                {status.learning_path_id && (
                  <button
                    onClick={() => canWriteLP && navigate(`/admin/learning-paths/${status.learning_path_id}`)}
                    disabled={!canWriteLP}
                    title={!canWriteLP ? 'You do not have permission to perform this action' : undefined}
                    className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all text-sm font-medium shadow-md hover:shadow-lg ${!canWriteLP ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Eye className="w-4 h-4" />
                    View Learning Path
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-semibold text-gray-700">Title</span>
                  </div>
                  <p className="text-gray-900 font-medium pl-6">{status.path_title}</p>
                </div>

                {status.description && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-semibold text-gray-700">Description</span>
                    </div>
                    <p className="text-gray-600 pl-6 text-sm leading-relaxed">{status.description}</p>
                  </div>
                )}

                {status.target_role_or_skill && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-semibold text-gray-700">Target Role/Skill</span>
                    </div>
                    <p className="text-gray-600 pl-6 text-sm">{status.target_role_or_skill}</p>
                  </div>
                )}

                {status.tags && status.tags.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-pink-500" />
                      <span className="text-sm font-semibold text-gray-700">Tags</span>
                    </div>
                    <div className="flex flex-wrap gap-2 pl-6">
                      {status.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs font-medium rounded-full border border-purple-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}
        </div>

        {/* Generation Stages */}
        <div className="space-y-4">
          {stages.map((stage, index) => {
            const stageData = getStageStatus(stage.key);
            const isExpanded = expandedStages.has(stage.key);
            const isActive = stageData?.status === 'processing';
            const isCompleted = stageData?.status === 'completed';
            const isFailed = stageData?.status === 'failed';
            const isSkipped = stageData?.status === 'skipped';
            const isLast = index === stages.length - 1;

            return (
              <div key={stage.key} className="relative">
                {/* Timeline Line */}
                {!isLast && (
                  <div
                    className={`absolute left-6 top-16 w-0.5 h-20 ${
                      isCompleted || isSkipped ? 'bg-gradient-to-b from-green-500 to-emerald-600' : 'bg-gray-300'
                    }`}
                  ></div>
                )}

                {/* Timeline Item */}
                <div className="flex gap-4">
                  {/* Status Circle */}
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2 relative ${
                      isCompleted
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100 border-green-500 shadow-lg'
                        : isSkipped
                        ? 'bg-gradient-to-br from-slate-100 to-gray-200 border-slate-400 shadow-md'
                        : isActive
                        ? 'bg-gradient-to-br from-blue-100 to-cyan-100 border-blue-500 shadow-lg animate-pulse'
                        : isFailed
                        ? 'bg-gradient-to-br from-red-100 to-rose-100 border-red-500 shadow-lg'
                        : 'bg-gradient-to-br from-gray-100 to-slate-100 border-gray-300'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : isSkipped ? (
                      <CheckCircle className="w-6 h-6 text-slate-500" />
                    ) : isActive ? (
                      <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                    ) : isFailed ? (
                      <XCircle className="w-6 h-6 text-red-600" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-400" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-8">
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300">
                      {/* Header */}
                      <div
                        className={`p-6 ${isExpanded ? 'border-b border-gray-100 rounded-t-2xl' : 'rounded-2xl'} ${
                          isActive || isFailed || isCompleted || isSkipped ? 'cursor-pointer hover:bg-opacity-80' : ''
                        } ${
                          isCompleted
                            ? 'bg-gradient-to-r from-green-50 to-emerald-50'
                            : isSkipped
                            ? 'bg-gradient-to-r from-slate-50 to-gray-100'
                            : isActive
                            ? 'bg-gradient-to-r from-blue-50 to-cyan-50'
                            : isFailed
                            ? 'bg-gradient-to-r from-red-50 to-rose-50'
                            : 'bg-gradient-to-r from-gray-50 to-slate-50'
                        }`}
                        onClick={isActive || isFailed || isCompleted || isSkipped ? () => toggleStageExpansion(stage.key) : undefined}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{stage.label}</h3>
                            <p className="text-sm text-gray-600">{stage.description}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Stage elapsed time */}
                            {stageElapsedTimes[stage.key] && (
                              <div
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md ${
                                  isActive
                                    ? 'bg-blue-100 text-blue-700 font-semibold'
                                    : isCompleted
                                    ? 'bg-green-100 text-green-700 font-medium'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                <Clock className={`w-3.5 h-3.5 ${isActive ? 'animate-spin' : ''}`} />
                                <span className="text-sm">{formatDuration(stageElapsedTimes[stage.key])}</span>
                                {isActive && <span className="animate-pulse text-blue-500">●</span>}
                              </div>
                            )}
                            <div
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                isCompleted
                                  ? 'bg-green-100 text-green-800'
                                  : isSkipped
                                  ? 'bg-slate-100 text-slate-700'
                                  : isActive
                                  ? 'bg-blue-100 text-blue-800'
                                  : isFailed
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {isCompleted ? 'Completed' : isSkipped ? 'Skipped' : isActive ? 'In Progress' : isFailed ? 'Failed' : 'Pending'}
                            </div>
                            {(isActive || isFailed || isCompleted || isSkipped) && (
                              <div className="text-gray-400 hover:text-gray-600">
                                {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expandable Content */}
                      {isExpanded && (
                        <>
                          {/* Progress Bar */}
                          {isActive && (
                            <div className="px-6 py-4 border-t border-gray-100 bg-gradient-to-r from-blue-50 to-cyan-50">
                              <div className="flex items-center justify-between text-sm text-blue-600 mb-2">
                                <span className="font-medium">Processing...</span>
                              </div>
                              <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                                <div
                                  className="h-3 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full animate-pulse"
                                  style={{ width: '65%' }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Completion Status */}
                          {isCompleted && (
                            <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 rounded-b-2xl">
                              <div className="flex items-center gap-2 text-green-800 mb-3">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-medium">Stage completed successfully</span>
                              </div>

                              {/* Stage 2: Extracted Skills Preview */}
                              {stage.key === 'stage_2_skill_extraction' && status.extracted_skills && status.extracted_skills.length > 0 && (
                                <div className="mt-3 p-4 bg-white rounded-lg border border-green-200">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Brain className="w-4 h-4 text-green-600" />
                                    <span className="font-medium text-green-800 text-sm">Extracted Skills</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {status.extracted_skills.slice(0, 10).map((skill: string, idx: number) => (
                                      <span
                                        key={idx}
                                        className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full border border-green-300"
                                      >
                                        {skill}
                                      </span>
                                    ))}
                                    {status.extracted_skills.length > 10 && (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full border border-green-300">
                                        +{status.extracted_skills.length - 10} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Stage 3: Metadata Sample */}
                              {stage.key === 'stage_3_metadata' && status.path_title && (
                                <div className="mt-3 p-4 bg-white rounded-lg border border-green-200">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Sparkles className="w-4 h-4 text-green-600" />
                                    <span className="font-medium text-green-800 text-sm">Generated Metadata</span>
                                  </div>
                                  <div className="space-y-2 text-sm">
                                    <div>
                                      <span className="font-semibold text-green-800">Title:</span>
                                      <p className="text-green-700 mt-1">{status.path_title}</p>
                                    </div>
                                    {status.description && (
                                      <div>
                                        <span className="font-semibold text-green-800">Description:</span>
                                        <p className="text-green-700 mt-1 line-clamp-2">{status.description}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Stage 4: Images Sample */}
                              {stage.key === 'stage_4_images' && (status.banner_image_url || status.thumbnail_image_url) && (
                                <div className="mt-3 p-4 bg-white rounded-lg border border-green-200">
                                  <div className="flex items-center gap-2 mb-3">
                                    <Image className="w-4 h-4 text-green-600" />
                                    <span className="font-medium text-green-800 text-sm">Generated Images</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {status.banner_image_url && (
                                      <div className="space-y-2">
                                        <p className="text-xs font-medium text-gray-600">Banner Image</p>
                                        <img
                                          src={status.banner_image_url}
                                          alt="Banner"
                                          className="rounded-lg border-2 border-gray-200 shadow-sm w-full h-auto hover:shadow-md transition-shadow"
                                        />
                                      </div>
                                    )}
                                    {status.thumbnail_image_url && (
                                      <div className="space-y-2">
                                        <p className="text-xs font-medium text-gray-600">Thumbnail Image</p>
                                        <img
                                          src={status.thumbnail_image_url}
                                          alt="Thumbnail"
                                          className="rounded-lg border-2 border-gray-200 shadow-sm w-full h-auto hover:shadow-md transition-shadow"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Stage 5: Courses Count */}
                              {stage.key === 'stage_5_courses' && status.stage_5_courses?.metadata?.courses_found !== undefined && (
                                <div className="mt-3 p-3 bg-white rounded-lg border border-green-200 text-sm text-green-700">
                                  <strong>Courses Found:</strong> {status.stage_5_courses.metadata.courses_found}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Error Status */}
                          {isFailed && (
                            <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-red-50 to-rose-50 rounded-b-2xl">
                              <div className="flex items-center gap-2 text-red-800 mb-2">
                                <XCircle className="w-5 h-5" />
                                <span className="font-medium">Stage failed</span>
                              </div>
                              {stageData?.error_details && (
                                <p className="text-sm text-red-700 mt-2 p-3 bg-white rounded-lg border border-red-200">
                                  {stageData.error_details}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Skipped Status */}
                          {isSkipped && (
                            <div className="p-6 border-t border-gray-100 bg-gradient-to-r from-slate-50 to-gray-100 rounded-b-2xl">
                              <div className="flex items-center gap-2 text-slate-700 mb-2">
                                <CheckCircle className="w-5 h-5 text-slate-500" />
                                <span className="font-medium">Stage skipped</span>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">
                                This stage was automatically skipped because Job Description text was provided directly.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

                  {/* No Courses Warning with Course Generation Suggestions */}
          {status.no_courses_found && (
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-yellow-900 mb-2">No Matching Courses Found</h3>
                  <p className="text-sm text-yellow-800 mb-4">
                    {status.no_courses_reason || 'AI could not find courses matching the extracted skills.'}
                  </p>

                  {/* Course Generation Suggestions */}
                  {status.course_generation_suggestions && status.course_generation_suggestions.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <h4 className="font-semibold text-yellow-900 flex items-center gap-2">
                        <PlusCircle className="w-5 h-5" />
                        Suggested Courses to Generate
                      </h4>
                      <p className="text-sm text-yellow-700 mb-3">
                        The following courses would help complete this learning path:
                      </p>
                      <div className="space-y-3">
                        {status.course_generation_suggestions.map((suggestion: any, index: number) => (
                          <div
                            key={index}
                            className="bg-white rounded-xl border border-yellow-200 p-4 hover:shadow-md transition-all"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="font-semibold text-gray-900">{suggestion.user_course_idea}</h5>
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      suggestion.importance === 'critical'
                                        ? 'bg-red-100 text-red-700'
                                        : suggestion.importance === 'high'
                                        ? 'bg-orange-100 text-orange-700'
                                        : suggestion.importance === 'medium'
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {suggestion.importance}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{suggestion.reason}</p>
                                <div className="flex flex-wrap gap-2 text-xs">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                                    <Users className="w-3 h-3 inline mr-1" />
                                    {suggestion.targeted_audience}
                                  </span>
                                  {suggestion.difficulty_level && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                      {suggestion.difficulty_level}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  if (!canBuildCourse) return;
                                  const params = new URLSearchParams({
                                    idea: suggestion.user_course_idea,
                                    audience: suggestion.targeted_audience,
                                    difficulty: suggestion.difficulty_level || 'intermediate',
                                  });
                                  navigate(`/admin/course/add-course?${params.toString()}`);
                                }}
                                disabled={!canBuildCourse}
                                title={!canBuildCourse ? 'You do not have permission to perform this action' : undefined}
                                className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors text-sm font-medium ${!canBuildCourse ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <Zap className="w-4 h-4" />
                                <span>Build Course</span>
                                <ArrowRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* Extracted Skills */}
        {status.extracted_skills && status.extracted_skills.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-900">Extracted Skills</h2>
              <span className="ml-auto text-sm text-gray-500">({status.extracted_skills.length} skills)</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {status.extracted_skills.map((skill: string, idx: number) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-sm font-medium rounded-full border border-purple-200"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-8 flex items-center justify-center gap-4">
          {status.overall_status === 'completed' ? (
            <div className="flex flex-col items-center gap-4">
              {/* Success Message */}
              <div className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl shadow-lg">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <p className="font-bold text-green-900 text-lg">All Stages Completed Successfully!</p>
                  <p className="text-sm text-green-700">
                    Your learning path is ready to be published and assigned to learners.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {status.learning_path_id && (
                  <button
                    onClick={() => canWriteLP && navigate(`/admin/learning-paths/${status.learning_path_id}`)}
                    disabled={!canWriteLP}
                    title={!canWriteLP ? 'You do not have permission to perform this action' : undefined}
                    className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-semibold shadow-lg hover:shadow-xl ${!canWriteLP ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Eye className="w-5 h-5" />
                    View Learning Path Details
                  </button>
                )}

                <button
                  onClick={() => {
                    if (!accessBothReadAndWrite ) return;
                    navigate("/admin/learning-paths");
                  }}
                  disabled={!accessBothReadAndWrite }
                  title={
                    !accessBothReadAndWrite 
                      ? "You do not have permission to perform this action"
                      : undefined
                  }
                  className={`inline-flex items-center gap-2 px-6 py-3 border-2 rounded-xl font-semibold transition-all shadow-lg
                    ${
                      accessBothReadAndWrite 
                        ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:shadow-xl cursor-pointer"
                        : "bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed opacity-60"
                    }`}
                >
                  <RouteIcon className="w-5 h-5" />
                  Go to Learning Paths
                </button>
              </div>
            </div>
          ) : status.overall_status === 'failed' ? (
            <div className="flex flex-col items-center gap-4">
              {/* Error Message */}
              <div className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl shadow-lg">
                <XCircle className="w-8 h-8 text-red-600" />
                <div>
                  <p className="font-bold text-red-900 text-lg">Workflow Failed</p>
                  <p className="text-sm text-red-700">
                    The workflow encountered an error. Please check the stage details above.
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <button
                onClick={() => navigate('/admin/learning-paths/workflow/create')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:from-orange-700 hover:to-red-700 transition-all font-semibold shadow-lg hover:shadow-xl"
              >
                <RefreshCw className="w-5 h-5" />
                Create New Workflow
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
